'use client';
import type React from 'react';
import { useEffect } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';

import { filtersListAtom } from '@core/Atoms/Plp/FiltersListAtom';
import { Grid } from '@gds/Grid/Grid';
import { areaAtom } from '@search/Atoms/AreaAtom';
import { makeAtom } from '@search/Atoms/MakeAtom';
import { maxMonthlyPriceAtom, maxTotalPriceAtom } from '@search/Atoms/MaxPriceAtom';
import { modelAtom } from '@search/Atoms/ModelAtom';
import { regionAtom } from '@search/Atoms/RegionAtom';
import { useSearchCtaUrl } from '@search/Hooks/UseSearchCtaUrl';

import BuyOnlineCta from './Components/BuyOnlineCta/BuyOnlineCta';
import { MakeModelWidget } from './Components/MakeModelWidget/MakeModelWidget';
import { MaxPrice } from './Components/MaxPrice/MaxPrice';
import Postcode from './Components/Postcode/Postcode';
import SearchCTA from './Components/SearchCTA/SearchCTA';
import type { SearchWidgetProps } from './Search.entity';
import { getDesktopItemGridSpan, getTabletItemGridSpan } from './Utils/GridUtils';
import { applySearchFilterToUrl } from './Utils/PriceUtils';

import styles from './Search.module.css';

const RegionAreaWidget = dynamic(() =>
  import('./Components/RegionAreaWidget/RegionAreaWidget').then(mod => mod.RegionAreaWidget),
);

const SearchWidget = ({
  hasBuyOnline,
  isCompactSearch,
  isReviewsMakePage,
  isReviewsPage,
  locationAttrs,
  locationCombinedParams,
  notInTab,
  plpPath,
  postcodeRequired,
  priceFilterType,
  reviewItems,
  strings,
}: SearchWidgetProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const isLocationEnabled: boolean = !!locationAttrs?.areas && !!locationAttrs?.regions;
  const { searchUrl, isFormValid } = useSearchCtaUrl(
    postcodeRequired,
    'uk',
    plpPath,
    isLocationEnabled,
  );
  const setActiveFiltersAtom = useSetAtom(filtersListAtom);
  const selectedMake = useAtomValue(makeAtom);
  const selectedMakeId = selectedMake.make;
  const selectedRegion = useAtomValue(regionAtom);
  const selectedArea = useAtomValue(areaAtom);
  const selectedModel = useAtomValue(modelAtom);
  const selectedTotalPrice = useAtomValue(maxTotalPriceAtom);
  const selectedMonthlyPrice = useAtomValue(maxMonthlyPriceAtom);
  const maxPrice = priceFilterType === 'monthly-price' ? selectedMonthlyPrice : selectedTotalPrice;
  const hasMake = selectedMakeId !== 'anymake';
  const hasModel = selectedModel?.key !== 'any_model';

  //Review slugs cant always be make up from make + model id (previous year model reviews, trims etc)
  //so we path the full review slug as the key for model
  const reviewSearchUrl = hasModel ? selectedModel?.key : `/cars/${selectedMakeId}/reviews`;

  //When aggregations are fetched they take the active Filters into account
  //So we need to remove them for accurate numbers
  useEffect(() => {
    setActiveFiltersAtom([]);
  }, []);

  const desktopItemGridSpan = getDesktopItemGridSpan(
    !!isCompactSearch,
    !!hasBuyOnline,
    postcodeRequired,
  );
  const tabletItemGridSpan = getTabletItemGridSpan(
    !!isCompactSearch,
    !!hasBuyOnline,
    postcodeRequired,
  );
  const buttonItemSpan = isCompactSearch ? 12 : 3;

  const buyOnlineCtaUrl = searchUrl.includes('ecom=true')
    ? searchUrl
    : searchUrl.includes('?')
      ? `${searchUrl}&ecom=true`
      : `${searchUrl}?ecom=true`;

  const isReviewsPageFormIncomplete = !hasMake || (pathname?.endsWith(selectedMakeId) && !hasModel);

  const isSearchDisabled =
    (isReviewsMakePage && isReviewsPageFormIncomplete) ||
    (!hasMake && isReviewsPage) ||
    ((selectedRegion.region === 'anyregion' || selectedArea.key === 'any_area') &&
      isLocationEnabled);

  const filteredUrl = applySearchFilterToUrl(
    isReviewsPage ? reviewSearchUrl : searchUrl,
    priceFilterType,
    maxPrice,
  );

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isLocationEnabled)
      return router.push(applySearchFilterToUrl(searchUrl, priceFilterType, maxPrice));
    if (!isFormValid && !isReviewsPage) return;
    router.push(filteredUrl);
  };

  return (
    <Grid.Container
      component="form"
      id="search-widget-form"
      className={`${styles.searchWrapper} ${notInTab ? styles.noParent : ''} ${
        isReviewsMakePage ? styles.wrapperWithText : ''
      } search-widget`}
      onSubmit={onSubmit}
    >
      <Grid.Row
        align="middle"
        gutter={{ mobile: 0, tablet: 0, desktop: 0 }}
        className={`${styles.searchWrapperRow} ${isCompactSearch ? styles.compactSearch : ''}`}
        data-price-filter={priceFilterType ?? undefined}
      >
        {isLocationEnabled && locationAttrs ? (
          <RegionAreaWidget
            locationAttrs={locationAttrs}
            strings={strings}
            tabletItemGridSpan={tabletItemGridSpan}
            desktopItemGridSpan={desktopItemGridSpan}
            locationCombinedParams={locationCombinedParams}
          />
        ) : (
          <MakeModelWidget
            strings={strings}
            tabletItemGridSpan={tabletItemGridSpan}
            desktopItemGridSpan={desktopItemGridSpan}
            isReviewsPage={isReviewsPage}
            reviewItems={reviewItems}
          />
        )}
        {priceFilterType && (
          <Grid.Col
            className={styles.searchCol}
            width={{ mobile: 12, tablet: 3, desktop: desktopItemGridSpan }}
          >
            <MaxPrice priceFilterType={priceFilterType} strings={strings} />
          </Grid.Col>
        )}
        {!isLocationEnabled && !isReviewsPage && (
          <Grid.Col
            className={`${styles.searchCol} ${styles.postcodeWrapper}`}
            width={{ mobile: 12, tablet: 3, desktop: desktopItemGridSpan }}
          >
            <Postcode
              className="searchWidgetPostcode"
              isRequired={postcodeRequired}
              placeholder={strings.postcodePlaceholder}
              validationError={strings.postcodeError}
              locale={'uk'}
            />
          </Grid.Col>
        )}

        <Grid.Col
          className={`${styles.searchCol} ${isLocationEnabled ? styles.searchCtaCol : ''}`}
          width={{ mobile: 12, tablet: buttonItemSpan, desktop: buttonItemSpan }}
        >
          <SearchCTA
            searchButton={isReviewsPage ? strings.viewReviews : strings.searchButton}
            loadingText={strings.loadingText}
            searchUrl={filteredUrl}
            locale={'uk'}
            disabled={isSearchDisabled}
            showLoadingCta={isReviewsPage || isLocationEnabled}
            isLocationPage={isLocationEnabled}
          />
        </Grid.Col>

        {hasBuyOnline && (
          <Grid.Col className={styles.searchCol} width={{ mobile: 12, tablet: 12, desktop: 3 }}>
            <BuyOnlineCta strings={strings} buyOnlineCtaUrl={buyOnlineCtaUrl} locale={'uk'} />
          </Grid.Col>
        )}
      </Grid.Row>
    </Grid.Container>
  );
};

export default SearchWidget;
